/**
 * Centralized pricing definitions for NimbleNode.
 *
 * TWO-TIER MODEL:
 *   Founders' Pricing: $29/mo (first 500 subscribers, locked for life)
 *   Standard: $39/mo
 * Both tiers unlock ALL features. Credits purchased separately.
 *
 * Legacy tier exports are kept below for backward compatibility with existing
 * components (Layout, Dashboard, CreditManager, etc.).
 */

// ─── New Pricing Model ─────────────────────────────────────────────────────────

/** One-time welcome credits for new users. */
export const WELCOME_CREDITS = 100;

/**
 * Subscription tiers — both unlock ALL features, no feature gating.
 */
export const BASE_SUBSCRIPTION = {
  founders: {
    name: "Founders' Pricing",
    planId: 'founders',
    monthlyPrice: 29,
    annualPrice: 23.20,
    annualTotal: 278.40,
    annualDiscount: 20, // percent
    badge: 'First 500 subscribers — locked for life',
  },
  standard: {
    name: 'Standard',
    planId: 'standard',
    monthlyPrice: 39,
    annualPrice: 31.20,
    annualTotal: 374.40,
    annualDiscount: 20, // percent
    badge: null,
  },
  // Convenience alias — default tier shown on landing page hero
  name: 'NimbleNode Pro',
  monthlyPrice: 29,
  annualPrice: 23.20,
  annualTotal: 278.40,
  annualDiscount: 20,
};

/** 14-day money-back guarantee applies to subscriptions only. */
export const MONEY_BACK_DAYS = 14;

/**
 * Credit packs — buy as needed, credits NEVER expire.
 */
export const CREDIT_PACKS = [
  { id: 'pack-500',   credits: 500,   price: 15,  name: '500 Credits',    label: null },
  { id: 'pack-1000',  credits: 1000,  price: 27,  name: '1,000 Credits',  label: null },
  { id: 'pack-2000',  credits: 2000,  price: 49,  name: '2,000 Credits',  label: 'Popular' },
  { id: 'pack-3000',  credits: 3000,  price: 69,  name: '3,000 Credits',  label: null },
  { id: 'pack-5000',  credits: 5000,  price: 109, name: '5,000 Credits',  label: 'Best Value' },
  { id: 'pack-10000', credits: 10000, price: 199, name: '10,000 Credits', label: null },
];

/**
 * Actions that cost ZERO credits (non-AI operations).
 */
export const FREE_ACTIONS = [
  'scheduling',
  'analytics',
  'calendar',
  'settings',
  'marketplace_browsing',
  'team_management',
];

/**
 * Returns the cheapest credit pack whose credit count covers the given
 * shortfall. Sorted ascending; the first match is the smallest sufficient
 * pack. Falls back to the largest pack when the shortfall exceeds all sizes.
 *
 * @param {number} shortfall - Credits still needed for an action.
 * @returns {object} A CREDIT_PACKS entry { id, credits, price, name, label }.
 */
export function cheapestCoveringPack(shortfall) {
  const sorted = [...CREDIT_PACKS].sort((a, b) => a.credits - b.credits);
  return sorted.find((p) => p.credits >= shortfall) ?? sorted[sorted.length - 1];
}

// ─── Legacy / Backward-Compatible Exports ───────────────────────────────────────
// These are kept so that Layout.jsx, Dashboard.jsx, CreditManager.jsx,
// CreditDisplay.jsx, and other existing components continue to work.
// They will be removed once all consumers are migrated.

/**
 * @deprecated Use BASE_SUBSCRIPTION + CREDIT_PACKS instead.
 */
export const TIER_ALLOCATIONS = {
  free: 0,
  starter: 500,
  creator: 2000,
  professional: 5000,
  business: 15000,
  enterprise: 50000,
};

/**
 * @deprecated Use BASE_SUBSCRIPTION + CREDIT_PACKS instead.
 */
export const TIERS = {
  free: {
    name: 'Free',
    monthlyCredits: TIER_ALLOCATIONS.free,
    price: 0,
    description: 'Try the platform',
    features: [
      `${WELCOME_CREDITS} welcome credits`,
      'Basic image generation',
      'Community support',
    ],
    cardColor: 'from-slate-500 to-slate-600',
    badgeColor: 'from-gray-400 to-gray-600',
    pillColor: 'bg-[#334155]',
  },
  pro: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: [
      'All AI generation tools',
      'Multi-platform publishing',
      'Advanced analytics',
      'Unlimited team members',
      'Priority support',
    ],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
  // Legacy keys mapped to pro for backward compat
  starter: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: ['All AI generation tools', 'Priority support'],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
  creator: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: ['All AI generation tools', 'Priority support'],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
  professional: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: ['All AI generation tools', 'Priority support'],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
  business: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: ['All AI generation tools', 'Priority support'],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
  enterprise: {
    name: 'NimbleNode Pro',
    monthlyCredits: 0,
    price: BASE_SUBSCRIPTION.monthlyPrice,
    description: 'All features included',
    features: ['All AI generation tools', 'Priority support'],
    cardColor: 'from-cyan-500 to-blue-500',
    badgeColor: 'from-cyan-400 to-cyan-600',
    pillColor: 'bg-[#06B6D4]',
  },
};

/** @deprecated */
export const getTierAllocation = (tier) => TIER_ALLOCATIONS[tier] || 0;

/** @deprecated */
export const getTierInfo = (tier) => TIERS[tier] || TIERS.free;

/** @deprecated */
export const getTierColor = (tier) => (TIERS[tier] || TIERS.free).pillColor;

/** @deprecated — Landing page no longer uses this. */
export const TIER_LIST = Object.entries(TIERS)
  .filter(([key]) => !['starter', 'creator', 'professional', 'business', 'enterprise'].includes(key))
  .map(([key, tier]) => ({
    key,
    ...tier,
  }));
